import classNames from "classnames";
import { useStoreState } from "pullstate";

import Artwork from "@/components/Players/Artwork";
import {
  getCurrentTrackSubtitle,
  getCurrentTrackTitle,
} from "@/helpers/getCurrentTrack";
import Container from "@/components/Container";
import Typography from "@/components/Typography";
import {
  PlayerStore,
  psPauseAudio,
  psPlayAudio,
  psPlayPreviousPodcast,
  psTogglePlayerControls,
  radioSource,
  SBMFFestivalSource,
} from "@/store/PlayerStore";
import { PlayerStatusTypes } from "@/helpers/pageHelpers/Common/interface";
import IconButton from "@/components/IconButton";
import PauseIcon from "@/icons/pauseLarge.svg";
import PlayIcon from "@/icons/playLarge.svg";
import StopIcon from "@/icons/stopLarge.svg";
import CloseIcon from "@/icons/arrowDownLarge.svg";
import Spinner from "@/components/Spinner";
import JumpForwardBackwardButton from "@/components/JumpForwardBackwardButton";
import { JumpDirecton } from "@/components/JumpForwardBackwardButton/JumpForwardBackwardButton.interface";
import AudioTimeline from "@/components/Players/AudioTimeline";
import { formatDate } from "@/helpers/formatDate";
import ScheduleTimeline from "@/components/Players/ScheduleTimeline";
import ContextualMenu from "@/components/ContextualMenu";
import { ContentSourceEnum } from "@/components/BookmarkButton/BookmarkButton.interface";
import { ContextualMenuEnum } from "@/components/ContextualMenu/ContextualMenu.interface";
import { useDataLayer } from "@/helpers/useDataLayer";
import PlaybackRateButton from "@/components/Players/PlaybackRateButton";

import styles from "./FullscreenPlayer.module.scss";

export const FullscreenPlayer: React.FC = () => {
  const {
    publishedAt,
    audioDuration,
    audioCurrentTime,
    showAdditionalControls,
    slug,
    podcast,
    isPlaying,
    isLoading,
    isEmptySource,
    isRadioSource,
    isAudioAvailable,
    isPodcastSource,
    isPreviousPodcastAvailable,
  } = useStoreState(PlayerStore, (store) => ({
    ...store,
    isPlaying: store.playerStatus === PlayerStatusTypes.Playing,
    isLoading: store.playerStatus === PlayerStatusTypes.Loading,
    isAudioAvailable: typeof window !== "undefined",
    isEmptySource: store.currentSource === "",
    isRadioSource:
      store.currentSource === radioSource ||
      store.currentSource === SBMFFestivalSource,
    isPodcastSource:
      !!store.currentSource?.length && store.currentSource !== radioSource,
    isPreviousPodcastAvailable: store.previousSource !== radioSource,
  }));
  const { pushToDataLayer } = useDataLayer();
  const currentTrackTitle = getCurrentTrackTitle();
  const currentTrackSubTitle = getCurrentTrackSubtitle();

  const isPodcast = isPodcastSource || isPreviousPodcastAvailable;
  const isScheduleVisible = (isRadioSource || isEmptySource) && !isPodcast;

  const pauseAudio = () => {
    PlayerStore.update(psPauseAudio);
  };

  const playAudio = () => {
    PlayerStore.update(
      isPreviousPodcastAvailable ? psPlayPreviousPodcast : psPlayAudio
    );
  };

  const close = () => {
    PlayerStore.update(psTogglePlayerControls);
    pushToDataLayer("hide_player");
  };

  return (
    <div
      className={classNames(
        styles.wrapper,
        showAdditionalControls && styles.visible
      )}
    >
      <Container className={styles.container}>
        <div className={styles.actions}>
          <IconButton className={styles.button} onClick={close}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.meta}>
          <Artwork className={styles.artwork} />

          <Typography variant="h3" component="p" className={styles.title}>
            {currentTrackTitle}
          </Typography>

          <Typography component="p" className={styles.subtitle}>
            {currentTrackSubTitle}
          </Typography>

          {isPodcast && publishedAt && (
            <Typography variant="medium" className={styles.publishedAt}>
              {formatDate(publishedAt)}
            </Typography>
          )}
        </div>
        <div>
          {isPodcast && !isRadioSource && (
            <>
              <PlaybackRateButton className={styles.rate} />
              <AudioTimeline withElapsedTime variant="fullscreen" />
            </>
          )}

          {isScheduleVisible && <ScheduleTimeline />}

          <div className={styles.controls}>
            {isPodcast && !isRadioSource && (
              <JumpForwardBackwardButton
                direction={JumpDirecton.Backward}
                isLarge
              />
            )}
            {isAudioAvailable && (
              <IconButton
                className={styles.playButton}
                onClick={isPlaying ? pauseAudio : playAudio}
              >
                {isLoading ? (
                  <Spinner className={styles.spinner} />
                ) : isPlaying ? (
                  isRadioSource ? (
                    <StopIcon />
                  ) : (
                    <PauseIcon />
                  )
                ) : (
                  <PlayIcon />
                )}
              </IconButton>
            )}
            {isPodcast && !isRadioSource && (
              <JumpForwardBackwardButton
                direction={JumpDirecton.Forward}
                isLarge
              />
            )}
          </div>
        </div>
      </Container>
      {isPodcast && !isRadioSource && (
        <ContextualMenu
          source={ContentSourceEnum.Episodes}
          variant={ContextualMenuEnum.Player}
          title={currentTrackTitle}
          subtitle={currentTrackSubTitle}
          className={styles.context}
          duration={audioDuration}
          episodeTime={audioCurrentTime}
          slug={slug}
          podcast={{ slug: podcast.slug }}
        />
      )}
    </div>
  );
};

export default FullscreenPlayer;
